<template>
  <div
    class="price-online-switch"
    :class="{ 'price-online-switch--v5': ff_lpu_branding_page_v5 }"
  >
    <v-switch
      :input-value="value"
      :color="switchColor"
      inset
      hide-details
      dense
      class="ma-0 mt-0 pa-0"
      data-qa="lpu_services_filter_online"
      @change="onChange"
    >
      <template #label>
        <span class="text-body-2 text--text text-no-wrap">
          {{ tStatic('Только услуги с онлайн-записью') }}
        </span>
      </template>
    </v-switch>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'

type Emits = {
  (_event: 'input', _value: boolean): void
}

defineProps<{
  value: boolean
}>()

const emit = defineEmits<Emits>()

const { context }: CommonContextStore = useContextStore()
const ff_lpu_branding_page_v5 = computed(() => (
  Boolean(context.request.feature_flags.ff_lpu_branding_page_v5)
))
const switchColor = computed(() => (
  ff_lpu_branding_page_v5.value
    ? 'var(--lpu-branding-color-text)'
    : 'var(--lpu-branding-color-primary)'
))

function onChange(value: boolean) {
  emit('input', value)
}
</script>

<style scoped lang="scss">
// Размеры: track 36×20, thumb 14×14, внутренний отступ 3px, ход 16px, gap до текста 12px.
$price-switch-track-width: 36px;
$price-switch-track-height: 20px;
$price-switch-thumb-size: 14px;
$price-switch-thumb-inset: 3px;
$price-switch-thumb-travel: 16px;
$price-switch-ripple-size: 28px;

.price-online-switch {
  :deep(.v-input--switch) {
    .v-input__slot {
      min-height: $price-switch-track-height;
    }

    .v-input--selection-controls__input {
      width: $price-switch-track-width;
      height: $price-switch-track-height;
      margin-right: 0;
    }

    .v-input--switch__track {
      width: $price-switch-track-width;
      height: $price-switch-track-height;
      left: 0;
      right: auto;
      top: calc(50% - #{$price-switch-track-height / 2});
      border-radius: $price-switch-track-height;
    }

    .v-input--switch__thumb {
      width: $price-switch-thumb-size;
      height: $price-switch-thumb-size;
      top: calc(50% - #{$price-switch-thumb-size / 2});
      margin: 0;
    }

    // Центр ripple = центр thumb; горизонтальный сдвиг только общим transform.
    // margin: 0 — у Vuetify на ripple margin: 7px, из‑за него круг съезжает вниз/вправо.
    .v-input--selection-controls__ripple {
      width: $price-switch-ripple-size;
      height: $price-switch-ripple-size;
      left: calc((#{$price-switch-thumb-size} - #{$price-switch-ripple-size}) / 2);
      top: calc(50% - #{$price-switch-ripple-size / 2});
      margin: 0;
    }

    .v-input--selection-controls__ripple,
    .v-input--switch__thumb {
      transform: translate($price-switch-thumb-inset, 0) !important;
    }

    &.v-input--is-dirty {
      .v-input--selection-controls__ripple,
      .v-input--switch__thumb {
        transform: translate(#{$price-switch-thumb-inset + $price-switch-thumb-travel}, 0) !important;
      }

      .v-input--switch__thumb {
        // Thumb — primary; !important против theme-класса primary--text у Vuetify.
        color: var(--lpu-branding-color-primary) !important;
      }

      // Track — primary 25%.
      .v-input--switch__track {
        color: color-mix(in sRGB, var(--lpu-branding-color-primary) 25%, transparent) !important;
        opacity: 1;
      }
    }
  }

  &--v5 {
    :deep(.v-input--switch.v-input--is-dirty .v-input--switch__thumb) {
      // Thumb — text при ff_lpu_branding_page_v5.
      color: var(--lpu-branding-color-text) !important;
    }
  }

  :deep(.v-label) {
    margin-left: 12px;
  }
}
</style>
